import { useMemo, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BackToTop from '@/components/feature/BackToTop';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SectionHeader from '@/components/feature/SectionHeader';
import MovieCard from '@/components/feature/MovieCard';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { movies, categories, getFeaturedMovies, getNewMovies, getHotMovies } from '@/mocks/movies';
import { faqs, stats } from '@/mocks/siteData';

export default function Home() {
  const navigate = useNavigate();
  const [currentBanner, setCurrentBanner] = useState(0);
  const featuredList = useMemo(() => getFeaturedMovies(), []);
  const newMovies = useMemo(() => getNewMovies(), []);
  const hotMovies = useMemo(() => getHotMovies(), []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentBanner((prev) => (prev + 1) % featuredList.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [featuredList.length]);

  const featured = featuredList[currentBanner];

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <SchemaOrg type="Organization" />
      <SchemaOrg type="WebSite" />
      <SchemaOrg type="WebPage" />
      <SchemaOrg type="BreadcrumbList" />
      <SchemaOrg type="FAQPage" data={{ faqs }} />

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
          {featuredList.map((movie, index) => (
            <div
              key={movie.id}
              className={`absolute inset-0 transition-opacity duration-800 ${
                index === currentBanner ? 'opacity-100 z-10' : 'opacity-0 z-0'
              }`}
            >
              <img
                src={movie.banner}
                alt=""
                className="w-full h-full object-cover object-top"
              />
            </div>
          ))}
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-background-50 z-20"></div>
          <div className="absolute inset-0 z-30 flex items-center">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-2xl">
                <div className="flex items-center gap-2 mb-4">
                  <span className="px-3 py-1 text-xs font-semibold bg-primary-500 text-background-50 rounded-full">
                    {featured?.quality}
                  </span>
                  <span className="text-xs text-foreground-200">
                    {featured?.genre.slice(0, 3).join(' · ')}
                  </span>
                </div>
                <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-50 mb-3">
                  <Link to={`/article/${featured?.id}`} className="hover:text-primary-400 transition-colors duration-200">
                    {featured?.title}
                  </Link>
                  <span className="block text-sm md:text-base font-normal text-foreground-300 mt-1">
                    {featured?.titleEn}
                  </span>
                </h1>
                <p className="text-sm md:text-base text-foreground-300 leading-relaxed mb-6 line-clamp-2 max-w-lg">
                  {featured?.description}
                </p>
                <div className="flex items-center gap-3">
                  <Link
                    to={`/article/${featured?.id}`}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-play-circle-fill text-lg"></i>
                    立即观看
                  </Link>
                  <div className="flex items-center gap-1 text-primary-400">
                    <i className="ri-star-fill"></i>
                    <span className="text-sm font-semibold">{featured?.rating}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2">
            {featuredList.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentBanner(index)}
                className={`w-2 h-2 rounded-full transition-all duration-300 cursor-pointer ${
                  index === currentBanner
                    ? 'bg-primary-500 w-6'
                    : 'bg-foreground-400/40 hover:bg-foreground-400/60'
                }`}
                aria-label={`切换到第${index + 1}张`}
              />
            ))}
          </div>
        </section>

        {/* Category Navigation */}
        <section className="w-full px-4 md:px-6 -mt-8 relative z-30">
          <div className="max-w-5xl mx-auto bg-background-100 rounded-xl p-4 md:p-6 border border-background-200/70">
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="flex flex-col items-center gap-2 p-3 rounded-lg hover:bg-background-200 transition-all duration-200 cursor-pointer group"
                >
                  <div className="w-10 h-10 md:w-11 md:h-11 flex items-center justify-center rounded-lg bg-background-200 text-foreground-400 group-hover:text-primary-500 group-hover:bg-primary-500/10 transition-all duration-200">
                    <i className={`${cat.icon} text-lg md:text-xl`}></i>
                  </div>
                  <span className="text-xs text-foreground-400 group-hover:text-foreground-50 transition-colors duration-200 whitespace-nowrap">
                    {cat.name}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-4">
            {stats.map((stat) => (
              <div
                key={stat.label}
                className="flex items-center gap-3 p-4 bg-background-100 rounded-lg border border-background-200/70"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-500/10 text-accent-500 shrink-0">
                  <i className={`${stat.icon} text-lg`}></i>
                </div>
                <div>
                  <div className="text-lg md:text-xl font-bold text-foreground-50">{stat.value}</div>
                  <div className="text-xs text-foreground-500">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Featured Movies */}
        <section className="w-full px-4 md:px-6 py-6 md:py-10">
          <div className="max-w-7xl mx-auto">
            <SectionHeader
              title="精选推荐"
              subtitle="口碑佳作，精选高清影视推荐"
              action={{ label: '查看全部', onClick: () => navigate('/category/all') }}
            />
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
              {featuredList.slice(0, 5).map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          </div>
        </section>

        {/* New Arrivals */}
        <section className="w-full px-4 md:px-6 py-6 md:py-10">
          <div className="max-w-7xl mx-auto">
            <SectionHeader
              title="最新上架"
              subtitle="第一时间更新，不错过任何精彩"
              action={{ label: '查看更多', onClick: () => navigate('/category/all') }}
            />
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
              {newMovies.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          </div>
        </section>

        {/* Hot Rankings */}
        <section className="w-full px-4 md:px-6 py-6 md:py-10">
          <div className="max-w-7xl mx-auto">
            <SectionHeader
              title="热门排行"
              subtitle="全网热播，大家都在看的高清影视"
              action={{ label: '完整榜单', onClick: () => navigate('/category/all') }}
            />
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
              {hotMovies.slice(0, 10).map((movie, index) => (
                <div key={movie.id} className="relative">
                  <div className="absolute top-2 left-2 z-10 w-6 h-6 flex items-center justify-center rounded-full bg-accent-500 text-background-50 text-xs font-bold">
                    {index + 1}
                  </div>
                  <MovieCard movie={movie} />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* All Content Grid */}
        <section className="w-full px-4 md:px-6 py-6 md:py-10">
          <div className="max-w-7xl mx-auto">
            <SectionHeader
              title="全部影视"
              subtitle="丰富片库，高清在线观看"
            />
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
              {movies.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
            <div className="flex justify-center mt-8">
              <Link
                to="/category/all"
                className="inline-flex items-center gap-2 px-6 py-2.5 border border-background-300 text-foreground-300 hover:text-foreground-50 hover:border-foreground-400 text-sm font-medium rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                浏览全部影视
                <i className="ri-arrow-right-line text-base"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <SectionHeader
              title="常见问题"
              subtitle="关于88影视网高清在线观看的常见疑问"
            />
            <div className="space-y-3">
              {faqs.map((faq, index) => (
                <details
                  key={index}
                  className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden cursor-pointer"
                >
                  <summary className="flex items-center justify-between px-5 py-4 text-sm font-medium text-foreground-100 list-none">
                    <span>{faq.question}</span>
                    <i className="ri-arrow-down-s-line text-lg text-foreground-500 group-open:rotate-180 transition-transform duration-200"></i>
                  </summary>
                  <div className="px-5 pb-4 text-sm text-foreground-500 leading-relaxed">
                    {faq.answer}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <BackToTop />
    </>
  );
}